import { useEffect, useState } from "react";
import { Loader2, Send, X, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const day = (s) => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`).toLocaleDateString();

function DraftCard({ d, onDone }) {
  const [f, setF] = useState({ subject: d.subject, message: d.message });
  const [busy, setBusy] = useState("");
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "send") await api.post(`/admin/nudge-drafts/${d.draft_id}/approve`, f);
      else await api.post(`/admin/nudge-drafts/${d.draft_id}/dismiss`);
      toast.success(kind === "send" ? `Nudge sent to ${d.email}` : "Draft dismissed");
      onDone(d.draft_id);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`nudge-draft-${d.draft_id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div><b className="text-slate-900">{d.company || d.name}</b> <span className="text-xs text-slate-500">{d.email}{d.industry ? ` · ${d.industry}` : ""}</span></div>
        <span className="text-xs text-amber-700 font-semibold">Stuck {d.stuck_days} days on "{d.stage_title}"</span>
      </div>
      <Input className="mt-3" value={f.subject} maxLength={150} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid={`nudge-draft-subject-${d.draft_id}`} />
      <Textarea className="mt-2" rows={6} maxLength={3000} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid={`nudge-draft-message-${d.draft_id}`} />
      <div className="flex gap-2 mt-3">
        <Button size="sm" disabled={!!busy || f.message.trim().length < 10} onClick={() => act("send")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`nudge-draft-approve-${d.draft_id}`}>{busy === "send" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Approve & send</Button>
        <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => act("dismiss")} data-testid={`nudge-draft-dismiss-${d.draft_id}`}><X className="h-4 w-4 mr-1" />Dismiss</Button>
      </div>
    </div>
  );
}

function AutoSettings() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/auto-nudge").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const toggle = async (k) => {
    const stages = cfg.stages.includes(k) ? cfg.stages.filter((x) => x !== k) : [...cfg.stages, k];
    try { const { data } = await api.put("/admin/auto-nudge", { stages }); setCfg({ ...cfg, stages: data.stages }); toast.success("Auto-approve updated"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="bg-slate-900 text-white rounded-lg p-5" data-testid="auto-nudge-settings">
      <div className="font-semibold">Auto-approve</div>
      <p className="text-xs text-slate-300 mt-1">For the stages you switch on, Vera sends her nudge right away instead of waiting here. You can also switch it on for individual customers from their journey board card ({cfg.customers.length} on now). Everything she sends is listed in your daily email.</p>
      <div className="flex flex-wrap gap-2 mt-3">
        {cfg.all_stages.map((s, i) => {
          const on = cfg.stages.includes(s.key);
          return <button key={s.key} onClick={() => toggle(s.key)} data-testid={`auto-stage-${s.key}`} aria-pressed={on}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-emerald-500 border-emerald-500 text-slate-900 font-semibold" : "border-white/20 text-slate-300 hover:border-white/50"}`}>{i + 1}. {s.title}{on ? " · auto" : ""}</button>;
        })}
      </div>
    </div>
  );
}

export function NudgeApprovals({ onCount }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/nudge-drafts").then((r) => setItems(r.data)).catch((e) => toast.error(formatErr(e))); }, []);
  useEffect(() => { if (items) onCount?.(items.length); }, [items, onCount]);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />;
  return (
    <div className="space-y-3" data-testid="nudge-approvals">
      <AutoSettings />
      <p className="text-sm text-slate-500">Every day Vera drafts a personal note for customers stuck on the same stage for 7+ days. Nothing is sent until you approve it.</p>
      {items.length === 0 ? <p className="text-sm text-slate-400 py-8 text-center" data-testid="nudge-approvals-empty">No drafts waiting. Vera checks again tomorrow.</p>
        : items.map((d) => <DraftCard key={d.draft_id} d={d} onDone={(id) => setItems((xs) => xs.filter((x) => x.draft_id !== id))} />)}
    </div>
  );
}

export function NudgeResults() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/nudge-results").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d || !d.nudged) return null;
  const tag = { advanced: "bg-emerald-50 text-emerald-700", no_change: "bg-slate-100 text-slate-600", waiting: "bg-amber-50 text-amber-700" };
  const label = { advanced: "Advanced", no_change: "No change", waiting: "Waiting (<7 days)" };
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-5" data-testid="nudge-results">
      <div className="flex flex-wrap items-center gap-4">
        <TrendingUp className="h-5 w-5 text-emerald-600" />
        <span className="font-semibold text-slate-900">Nudge results (last 60 days)</span>
        <span className="text-sm text-slate-600" data-testid="nudge-results-summary">{d.nudged} nudged · {d.advanced} advanced within a week{d.rate != null ? ` · ${d.rate}% success` : ""}{d.waiting ? ` · ${d.waiting} still in their first week` : ""}</span>
      </div>
      <div className="mt-3 divide-y divide-slate-100 text-xs">
        {d.rows.slice(0, 12).map((r, i) => (
          <div key={i} className="py-2 flex flex-wrap items-center gap-3" data-testid={`nudge-result-${i}`}>
            <span className="w-48 truncate text-slate-800">{r.name || r.email}</span>
            <span className="text-slate-500 w-24">{day(r.nudged_at)}</span>
            <span className="flex-1 text-slate-600 truncate">{r.stage_at_nudge} → {r.current_stage}</span>
            <span className={`px-2 py-0.5 rounded-full font-semibold ${tag[r.result]}`}>{label[r.result]}{r.advanced_at ? ` ${day(r.advanced_at)}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
